import { z } from 'zod';

import { reportBrokenField, reportedField } from './tolerant-rows';

/** The four reasons api 1.156.0 (BE-002B) documents for a null `cagr`. */
const CAGR_NULL_REASONS = [
  'SPAN_UNDER_ONE_YEAR',
  'INSUFFICIENT_DATA',
  'UNUSABLE_BASE',
  'NON_FINITE'
] as const;

type CagrNullReason = (typeof CAGR_NULL_REASONS)[number];

/**
 * Why a served `cagr` (and `calmarRatio`) is null, beside it on every
 * performance `metrics` block since v1.156.0 — the backtest, vault, index
 * and investor performance routes alike — and null whenever it is a number.
 *
 * Strict, as the contract is: one of the four documented reasons, or
 * null/absent (an older API). Anything else is a contract break, reported
 * once under `label` and read as no reason, so the screen shows its generic
 * note and keeps the rest of the response.
 */
export function cagrNullReasonField(label: string) {
  return reportedField(z.enum(CAGR_NULL_REASONS), {
    label,
    path: 'cagrNullReason',
    invalid: null
  });
}

/**
 * The contract between `cagr` and its reason, checked on the whole metrics
 * block: the reason is null whenever `cagr` is a number. A block serving
 * both contradicts itself, and an annual figure the backend says should
 * not exist must not be shown: the break is reported once under `label`
 * (by field name), and the block reads as if neither was served — no
 * `cagr`, no reason, and no `calmarRatio`, which is gated with `cagr` and
 * divides by it. Every other figure is kept.
 *
 * Use it as the block's transform, after `cagrNullReasonField(label)`.
 */
export function consistentCagr(label: string) {
  return <
    Metrics extends {
      cagr?: number | null;
      cagrNullReason?: CagrNullReason | null;
    }
  >(
    metrics: Metrics
  ): Metrics => {
    if (metrics.cagr == null || metrics.cagrNullReason == null) return metrics;
    reportBrokenField(label, 'cagr');
    return {
      ...metrics,
      cagr: null,
      cagrNullReason: null,
      ...('calmarRatio' in metrics ? { calmarRatio: null } : {})
    };
  };
}
